import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
// Regular imports for lightweight validation components
import ProtectedRoute from "./components/pretectedRoute";
import ProtectedSuperuserRoute from "./components/ProtectedSuperuser";
import PermissionsUserRouter from "./components/PermissionUser";
import SpinnerMain from "./components/Spinner";
ChartJS.register(...registerables);

// Lazy-loaded components
const LogIn = lazy(() => import("./pages/login"));
const NotFound = lazy(() => import("./pages/notFound"));
const NoPermissionPage = lazy(() => import("./components/NoPermission"));
const Home = lazy(() => import("./pages/home"));
const BookingCn = lazy(() => import("./pages/challan_create"));
const AccountCreateForm = lazy(() => import("./pages/accounts"));
const IncomingList = lazy(() => import("./pages/incoming_chllan"));
const OutgoingList = lazy(() => import("./pages/outgoing_challans"));
const ManifestList = lazy(() => import("./pages/manifest_list"));
const ManifestCreate = lazy(() => import("./pages/manifest_create"));
const PaymentCreate = lazy(() => import("./pages/payment_create"));
const IncomingReport = lazy(() => import("./pages/incoming_report"));
const AdminHome = lazy(() => import("./pages/admin_home"));
const ChallanSpecification = lazy(() => import("./pages/challan_specification"));
const ChallanViewPage = lazy(() => import("./pages/ChallanViewPage"));
const PackingMain = lazy(() => import("./pages/packing"));
const PackingList = lazy(() => import("./pages/packing_list"));
const PackingDetails = lazy(() => import("./pages/packing_details"));
const LoadingCreate = lazy(() => import("./pages/loading_create"));
const LoadingView = lazy(() => import("./pages/loading_view"));
const LoadingList = lazy(() => import("./pages/loading_list"));
const InvoiceList = lazy(() => import("./pages/invoice_list"));
const InvoiceView = lazy(() => import("./pages/invoice_view"));
const InvoiceMain = lazy(() => import("./pages/invoice_create"));
const InvoiceRateChange = lazy(() => import("./pages/invoice_rate_change"));
const DeliveryList = lazy(() => import("./pages/delivery_list"));
const DeliveryCreate = lazy(() => import("./pages/delivery_create"));
const ReceiveCreate = lazy(() => import("./pages/receive_create"));
const ContraCreate = lazy(() => import("./pages/contra_create"));
const PurchaseCreate = lazy(() => import("./pages/purchase_create"));
const LedgerList = lazy(() => import("./pages/ledger_list"));
const LedgerMain = lazy(() => import("./pages/ledger_main"));
const DeliverySecond = lazy(() => import("./pages/deliver_second"));
const ReceiveList = lazy(() => import("./pages/receive_list"));
const MultipleCreate = lazy(() => import("./pages/multiple_receive"));
const MultipelSecond = lazy(() => import("./pages/multiple_second"));
const SalesCreate = lazy(() => import("./pages/sales_create"));
const JournalCreate = lazy(() => import("./pages/journal"));
const AccountList = lazy(() => import("./pages/account_list"));
const DailySheet = lazy(() => import("./pages/daily_sheet"));
const OutgoingReport = lazy(() => import("./pages/outgoing_report"));
const DeliveryReport = lazy(() => import("./pages/delivery_report"));
const ManifestReport = lazy(() => import("./pages/manifest_report"));
const ReceiveReport = lazy(() => import("./pages/receive_report"));
const PaymentReport = lazy(() => import("./pages/payment_report"));
const PurchaseReport = lazy(() => import("./pages/purchase_report"));
const SalesReport = lazy(() => import("./pages/sales_report"));
const JournalReport = lazy(() => import("./pages/journal_report"));
const ContraReport = lazy(() => import("./pages/contra_report"));
const GSTR1Report = lazy(() => import("./pages/gstr1_report"));
const ManifestView = lazy(() => import("./pages/manifest_view"));
const DeliveryView = lazy(() => import("./pages/delivery_view"));
const ReceiveView = lazy(() => import("./pages/receive_view"));
const RegistrationForm = lazy(() => import("./pages/user_create"));
const UnloadingList = lazy(() => import("./pages/unloading_list"));
const UnloadingView = lazy(() => import("./pages/unloading_view"));
const UnloadingCreate = lazy(() => import("./pages/unloading_create"));
const LeaseList = lazy(() => import("./pages/lease_list"));
const ChequeList = lazy(() => import("./pages/cheque_list"));
const VehicleList = lazy(() => import("./pages/vehicle_list"));
const ExpenseList = lazy(() => import("./pages/expense_list"));
const RateChartUser = lazy(() => import("./pages/rate_chart_user"));
const EwayBillExpireReport = lazy(() => import("./pages/eway_bill_expire"));
// Admin
const AdminPayment = lazy(() => import("./pages/payment_edit"));
const AssianRole = lazy(() => import("./pages/assian_role"));
const UserList = lazy(() => import("./pages/user_list"));
const UserPermissions = lazy(() => import("./pages/user_permission"));
const AssianBranch = lazy(() => import("./pages/assian_branch"));
const AdminPurchase = lazy(() => import("./pages/purchase_edit"));
const AdminSales = lazy(() => import("./pages/sales_edit"));
const AdminJournal = lazy(() => import("./pages/journal_edit"));
const AdminContra = lazy(() => import("./pages/contra_edit"));
const AdminChallan = lazy(() => import("./pages/all_challan"));
const ChallanEdit = lazy(() => import("./pages/chllan_edit"));
const AdminAccountEdit = lazy(() => import("./pages/account_edit"));
const AdminReceive = lazy(() => import("./pages/receive_edit"));
const ReceiveEdit = lazy(() => import("./components/ReceiveEdit"));
const AdminPacking = lazy(() => import("./pages/admin_packing"));
const PackingEdit = lazy(() => import("./components/packing_edit"));
const AdditionalChargesList = lazy(() => import("./pages/additional_charges_list"));
const SerialList = lazy(() => import("./pages/serial_list"));
const AdminManifest = lazy(() => import("./pages/admin_manifest_list"));
const ManifestEdit = lazy(() => import("./pages/manifest_edit"));
const AdminLoading = lazy(() => import("./pages/admin_loading"));
const AdminUnloading = lazy(() => import("./pages/admin_unloading"));
const LoadingEdit = lazy(() => import("./pages/loading_edit"));
const UnloadingEdit = lazy(() => import("./pages/unloading_edit"));
const AdminDelivery = lazy(() => import("./pages/admin_delivery"));
const DeliveryEdit = lazy(() => import("./pages/delivery_edit"));
const AdminInvoice = lazy(() => import("./pages/admin_invoice"));
const InvoiceEdit = lazy(() => import("./pages/invoice_edit"));
const AdminRateChart = lazy(() => import("./pages/rate_chart"));
// Logout component (small, kept inline)
function LogOut() {
    localStorage.clear();
    return <Navigate to="/" />;
}

// Centralized loading fallback
const LoadingFallback = () => (
    <div>
        <SpinnerMain />
    </div>
);

function App() {
    return (
        <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    <Route
                        path="*"
                        element={<NotFound />}
                    />
                    <Route
                        path="/"
                        element={<LogIn title="LogIn" />}
                    />
                    <Route
                        path="/logout"
                        element={<LogOut />}
                    />
                    <Route
                        path="/no_permission"
                        element={
                            <ProtectedRoute>
                                <NoPermissionPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/user_create"
                        element={
                            <ProtectedRoute>
                                <RegistrationForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/home"
                        element={
                            <ProtectedRoute>
                                <Home title="Home" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/accounts_create"
                        element={
                            <ProtectedRoute>
                                <AccountCreateForm />
                            </ProtectedRoute>
                        }
                    />
                    {/* Challan */}
                    <Route
                        path="/challan_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={1}
                                    type={1}
                                >
                                    <BookingCn title="Challan | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/incoming_challans"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={1}
                                    type={1}
                                >
                                    <IncomingList title="Incoming Challans | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outgoing_challans"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={1}
                                    type={1}
                                >
                                    <OutgoingList title="Outgoing Challans | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/challan_view/:slug"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={1}
                                    type={1}
                                >
                                    <ChallanViewPage />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/challan_specification/:slug"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={1}
                                    type={1}
                                >
                                    <ChallanSpecification />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    {/* Manifest and Tracking */}
                    <Route
                        path="/packing_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={2}
                                    type={1}
                                >
                                    <PackingList title="Packing | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/packing_details/:pk"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={2}
                                    type={1}
                                >
                                    <PackingDetails />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/packing_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={2}
                                    type={1}
                                >
                                    <PackingMain title="Packing | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manifest_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={3}
                                    type={1}
                                >
                                    <ManifestList title="Manifest | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manifest_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={3}
                                    type={1}
                                >
                                    <ManifestCreate title="Manifest | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manifest_view/:pk"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={3}
                                    type={1}
                                >
                                    <ManifestView />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/loading_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={4}
                                    type={1}
                                >
                                    <LoadingCreate title="Loading | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/loading_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={4}
                                    type={1}
                                >
                                    <LoadingList title="Loading | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/loading_view/:pk"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={4}
                                    type={1}
                                >
                                    <LoadingView />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/unloading_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={5}
                                    type={1}
                                >
                                    <UnloadingList title="Unloading | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/unloading_view/:pk"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={5}
                                    type={1}
                                >
                                    <UnloadingView />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/unloading_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={5}
                                    type={1}
                                >
                                    <UnloadingCreate title="Unloading | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lease_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={12}
                                    type={1}
                                >
                                    <LeaseList title="Lease | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/cheque_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={13}
                                    type={1}
                                >
                                    <ChequeList title="Cheque | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/expense_list"
                        element={
                            <ProtectedRoute>
                                <ExpenseList title="Expense | List" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/vehicle_list"
                        element={
                            <ProtectedRoute>
                                <VehicleList title="Expense | List" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rate_chart_user"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={14}
                                    type={1}
                                >
                                    <RateChartUser title="Rate Chart | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/eway_bill_expire_report"
                        element={
                            <ProtectedRoute>
                                <EwayBillExpireReport title="Eway Bill Expire | Report" />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/delivery_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={6}
                                    type={1}
                                >
                                    <DeliveryList title="Delivery | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={6}
                                    type={1}
                                >
                                    <DeliveryCreate title="Delivery | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery_second"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={6}
                                    type={1}
                                >
                                    <DeliverySecond title="Delivery | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery_view/:slug"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={6}
                                    type={1}
                                >
                                    <DeliveryView />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    {/* Voucher */}
                    <Route
                        path="/payment_create"
                        element={
                            <ProtectedRoute>
                                <PaymentCreate title="Payment | Create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/receive_create"
                        element={
                            <ProtectedRoute>
                                <ReceiveCreate title="Receive | Create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/receive_view/:pk"
                        element={
                            <ProtectedRoute>
                                <ReceiveView />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contra_create"
                        element={
                            <ProtectedRoute>
                                <ContraCreate title="Contra | Create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/purchase_create"
                        element={
                            <ProtectedRoute>
                                <PurchaseCreate title="Purchase | Create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales_create"
                        element={
                            <ProtectedRoute>
                                <SalesCreate title="Sales | Create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/journal_create"
                        element={
                            <ProtectedRoute>
                                <JournalCreate title="Journal | Create" />
                            </ProtectedRoute>
                        }
                    />
                    {/* Accounts */}
                    <Route
                        path="/account_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={8}
                                    type={1}
                                >
                                    <AccountList title="Account | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoice_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={11}
                                    type={1}
                                >
                                    <InvoiceList title="Invoice | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoice_view/:pk"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={11}
                                    type={1}
                                >
                                    <InvoiceView />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoice_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={11}
                                    type={1}
                                >
                                    <InvoiceMain title="Invoice | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoice_rate_change"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={11}
                                    type={1}
                                >
                                    <InvoiceRateChange title="Invoice | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/receive_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={7}
                                    type={1}
                                >
                                    <ReceiveList title="Receive | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/multiple_create"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={7}
                                    type={1}
                                >
                                    <MultipleCreate title="Receive | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/multiple_second"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={7}
                                    type={1}
                                >
                                    <MultipelSecond title="Receive | Create" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dailysheet"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={9}
                                    type={1}
                                >
                                    <DailySheet title="Dailysheet | View" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ledger_list"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={10}
                                    type={1}
                                >
                                    <LedgerList title="Ledger | List" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ledger_main"
                        element={
                            <ProtectedRoute>
                                <PermissionsUserRouter
                                    column={10}
                                    type={1}
                                >
                                    <LedgerMain title="Ledger | View" />
                                </PermissionsUserRouter>
                            </ProtectedRoute>
                        }
                    />
                    {/* Reports */}
                    <Route
                        path="/incoming_report"
                        element={
                            <ProtectedRoute>
                                <IncomingReport title="Incoming | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outgoing_report"
                        element={
                            <ProtectedRoute>
                                <OutgoingReport title="Outgoing | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery_report"
                        element={
                            <ProtectedRoute>
                                <DeliveryReport title="Delivery | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manifest_report"
                        element={
                            <ProtectedRoute>
                                <ManifestReport title="Manifest | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/receive_report"
                        element={
                            <ProtectedRoute>
                                <ReceiveReport title="Receive | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/payment_report"
                        element={
                            <ProtectedRoute>
                                <PaymentReport title="Payment | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/purchase_report"
                        element={
                            <ProtectedRoute>
                                <PurchaseReport title="Purchase | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales_report"
                        element={
                            <ProtectedRoute>
                                <SalesReport title="Sales | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/journal_report"
                        element={
                            <ProtectedRoute>
                                <JournalReport title="Journal | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contra_report"
                        element={
                            <ProtectedRoute>
                                <ContraReport title="Contra | Report" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/gst_r1_report"
                        element={
                            <ProtectedRoute>
                                <GSTR1Report title="Gst R1 | Report" />
                            </ProtectedRoute>
                        }
                    />
                    {/* Admin */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminHome title="Admin | Home" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/assian_branch"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AssianBranch title="Assian Branch | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/assian_role"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AssianRole title="Assian Role | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/user_list"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <UserList title="User | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/permission_user/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <UserPermissions title="Permission User | Create" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/payment_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminPayment title="Payment | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/purchase_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminPurchase title="Purchase | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminSales title="Sales | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/journal_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminJournal title="Journal | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contra_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminContra title="Contra | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_challan"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminChallan title="Challans | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/challan_edit/:slug"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <ChallanEdit title="Challans | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/account_edit"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminAccountEdit title="Account | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_receive"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminReceive title="Receive | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/receive_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <ReceiveEdit title="Receive | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* Tracking Edit */}
                    <Route
                        path="/admin_packing"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminPacking title="Packing | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/packing_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <PackingEdit title="Packing | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_manifest"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminManifest title="Manifest | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manifest_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <ManifestEdit title="Manifest | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_loading"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminLoading title="Loading | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/loading_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <LoadingEdit title="Loading | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_unloading"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminUnloading title="Unloading | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/unloading_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <UnloadingEdit title="Unloading | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_delivery"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminDelivery title="Delivery | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <DeliveryEdit title="Delivery | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_invoice"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminInvoice title="Invoice | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoice_edit/:pk/"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <InvoiceEdit title="Invoice | Edit" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rate_chart_list"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdminRateChart title="Rate Chart | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/additional_charge"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <AdditionalChargesList title="Additional Charge | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin_serial"
                        element={
                            <ProtectedRoute>
                                <ProtectedSuperuserRoute>
                                    <SerialList title="Serial | List" />
                                </ProtectedSuperuserRoute>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
}

export default App;
